import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    Platform,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextAreaInput,
    SelectBox,
    Seperator,
    TextInput,
    Dialog,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    EventType
} from "../../type";
import type {
    RouteProp
} from "@react-navigation/native";
import {
    ArrowLeftIcon,
    TrashIcon,
    ImageIcon,
    SaveIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import RESTServiceContext from "../../../../services/restAPI/context";

type EventEditRouteProp = RouteProp<RootStackParamList, "EventEdit">;

const EventEdit = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute<EventEditRouteProp>();
    const eventID = route.params?.eventID;

    const [
        title,
        setTitle
    ] = useState<string>("");

    const [
        description,
        setDescription
    ] = useState<string>("");

    const [
        type,
        setType
    ] = useState<EventType>("standart");

    const [
        isOnlyMembers,
        setIsOnlyMembers
    ] = useState<boolean>(false);

    const [
        hasSubEvents,
        setHasSubEvents
    ] = useState<boolean>(false);

    const [
        moduleName,
        setModuleName
    ] = useState<string>("");

    const [
        logoPreview,
        setLogoPreview
    ] = useState<string>("");

    const [
        coverPreview,
        setCoverPreview
    ] = useState<string>("");

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const [
        isDeleteDialogVisible,
        setIsDeleteDialogVisible
    ] = useState<boolean>(false);

    const [
        isDeleting,
        setIsDeleting
    ] = useState<boolean>(false);

    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const coverInputRef = useRef<HTMLInputElement | null>(null);
    const [
        logoFile,
        setLogoFile
    ] = useState<File | null>(null);
    const [
        coverFile,
        setCoverFile
    ] = useState<File | null>(null);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        const fetchEventDetail = async () => {
            if (!eventID) {
                setIsLoading(false);
                return;
            }
            try {
                const result = await RESTServiceContext.action("getEventForAdmin", {
                    eventID
                });

                const evResult = result as unknown as { payload?: { title: string, description?: string, type?: Parameters<typeof setType>[0], isOnlyMembers?: boolean, logoPath?: string, coverPhotoPath?: string, moduleName?: string, activeEvents?: { _id: string }[], pastEvents?: { _id: string }[] } };
                if (evResult.payload) {
                    setTitle(evResult.payload.title);
                    setDescription(evResult.payload.description || "");
                    setType(evResult.payload.type || "standart");
                    setIsOnlyMembers(Boolean(evResult.payload.isOnlyMembers));

                    if (evResult.payload.logoPath) {
                        setLogoPreview(evResult.payload.logoPath);
                    }
                    if (evResult.payload.coverPhotoPath) {
                        setCoverPreview(evResult.payload.coverPhotoPath);
                    }
                    if (evResult.payload.moduleName) {
                        setModuleName(evResult.payload.moduleName);
                    }
                    const activeCount = evResult.payload.activeEvents?.length || 0;
                    const pastCount = evResult.payload.pastEvents?.length || 0;
                    if (activeCount > 0 || pastCount > 0) {
                        setHasSubEvents(true);
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEventDetail();
    }, [
        eventID
    ]);

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setCoverFile(file);
            setCoverPreview(URL.createObjectURL(file));
        }
    };

    const uploadPhoto = async (
        targetEventID: string,
        file: File,
        tokenEndpoint: string
    ) => {
        try {
            const tokenData = await RESTServiceContext.action(tokenEndpoint as Parameters<typeof RESTServiceContext.action>[0], {
                eventID: targetEventID
            });
            const uploadResult = tokenData as unknown as { payload?: { token?: string; uploadToken?: string } };
            const token = uploadResult.payload?.token || uploadResult.payload?.uploadToken;
            if (token) {
                const formData = new FormData();
                formData.append("file", file);
                await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: token
                    },
                    body: formData,
                    method: "POST"
                });
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        try {
            setIsSubmitting(true);
            if (eventID) {
                const result = await RESTServiceContext.action("editEvent", {
                    isOnlyMembers: isOnlyMembers,
                    description: description,
                    moduleName: moduleName,
                    eventID: eventID,
                    title: title,
                    type: type
                });
                const editResult = result as unknown as { code: number, payload?: unknown };
                if (editResult.code === 200 || editResult.payload) {
                    if (logoFile) {
                        await uploadPhoto(eventID, logoFile, "getUploadTokenForEventLogo");
                    }
                    if (coverFile) {
                        await uploadPhoto(eventID, coverFile, "getUploadTokenForEventCover");
                    }
                    NCoreUIKitSnackBar.open({
                        title: localize("events-success-save")
                    });
                    navigation.navigate("EventsList");
                } else {
                    NCoreUIKitSnackBar.open({
                        title: localize("events-error-save")
                    });
                }
            } else {
                const result = await RESTServiceContext.action("createEvent", {
                    isOnlyMembers: isOnlyMembers,
                    description: description,
                    moduleName: moduleName,
                    title: title,
                    type: type
                });
                const createResult = result as unknown as { code: number, payload?: { id?: string } };
                if (createResult.code === 200 || createResult.payload) {
                    const newEventID = createResult.payload?.id;
                    if (newEventID) {
                        if (logoFile) {
                            await uploadPhoto(newEventID, logoFile, "getUploadTokenForEventLogo");
                        }
                        if (coverFile) {
                            await uploadPhoto(newEventID, coverFile, "getUploadTokenForEventCover");
                        }
                    }
                    NCoreUIKitSnackBar.open({
                        title: localize("events-success-create")
                    });
                    navigation.navigate("EventsList");
                } else {
                    NCoreUIKitSnackBar.open({
                        title: localize("events-error-save")
                    });
                }
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("events-error-save")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const result = await RESTServiceContext.action("deleteEvent", {
                eventID: eventID as string
            });
            const deleteResult = result as unknown as { code: number, payload?: unknown, message?: string };
            if (deleteResult.code === 200 || deleteResult.code === 201 || deleteResult.payload) {
                NCoreUIKitSnackBar.open({
                    title: localize("events-delete-success")
                });
                navigation.navigate("EventsList");
            } else {
                NCoreUIKitSnackBar.open({
                    title: deleteResult.message || "Error"
                });
            }
        } catch (error) {
            NCoreUIKitSnackBar.open({
                title: "Network Error"
            });
            console.error(error);
        } finally {
            setIsDeleting(false);
            setIsDeleteDialogVisible(false);
        }
    };

    const handleSubmit = async () => {
        if (!title.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("events-title-required")
            });
            return;
        }

        if (!description.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("events-description-required")
            });
            return;
        }

        setIsDialogVisible(true);
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                icon={() => <ArrowLeftIcon
                    color={colors.project.brandWhite}
                    size={24}
                />}
                customBorderColor="secondaryButton"
                onPress={() => navigation.goBack()}
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
            />
            <Text
                variant="titleLargeSize"
            >
                {localize("events-edit-page-title")}
            </Text>
        </View>;
    };

    const renderImagesSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                stylesheet.formCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-images-section-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-images-section-subtitle")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.uploadRow,
                    {
                        flexDirection: isMobile ? "column" : "row"
                    }
                ]}
            >
                {
                    Platform.OS === "web" ?
                        <input
                            onChange={handleLogoSelect}
                            style={{
                                display: "none"
                            }}
                            ref={logoInputRef}
                            accept="image/*"
                            type="file"
                        />
                    :
                        null
                }
                {
                    Platform.OS === "web" ?
                        <input
                            onChange={handleCoverSelect}
                            style={{
                                display: "none"
                            }}
                            ref={coverInputRef}
                            accept="image/*"
                            type="file"
                        />
                    :
                        null
                }
                <TouchableOpacity
                    style={[
                        stylesheet.logoUploadBox,
                        {
                            marginBottom: isMobile ? spaces.spacingLg : 0,
                            backgroundColor: colors.project.brandSurface,
                            marginRight: isMobile ? 0 : spaces.spacingLg,
                            borderColor: colors.project.brandBorder,
                            width: isMobile ? "100%" : 160,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => logoInputRef.current?.click()}
                    activeOpacity={0.8}
                >
                    {
                        logoPreview ?
                            <Image
                                style={stylesheet.uploadedImage}
                                source={{
                                    uri: logoPreview
                                }}
                                resizeMode="cover"
                            />
                        :
                            <View
                                style={[
                                    stylesheet.uploadPlaceholder,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <ImageIcon
                                    color={colors.content.icon.mid}
                                    size={32}
                                />
                                <Text
                                    style={stylesheet.uploadLabel}
                                    variant="bodyMediumSize"
                                >
                                    {localize("events-upload-logo")}
                                </Text>
                                <Text
                                    style={[
                                        stylesheet.uploadDimensions,
                                        {
                                            color: colors.content.text.low
                                        }
                                    ]}
                                    variant="labelSmallSize"
                                >
                                    {localize("events-logo-dimensions")}
                                </Text>
                            </View>
                    }
                </TouchableOpacity>
                <TouchableOpacity
                    style={[
                        stylesheet.coverUploadBox,
                        {
                            backgroundColor: colors.project.brandSurface,
                            borderColor: colors.project.brandBorder,
                            width: isMobile ? "100%" : undefined,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => coverInputRef.current?.click()}
                    activeOpacity={0.8}
                >
                    {
                        coverPreview ?
                            <Image
                                style={stylesheet.uploadedImage}
                                source={{
                                    uri: coverPreview
                                }}
                                resizeMode="cover"
                            />
                        :
                            <View
                                style={[
                                    stylesheet.uploadPlaceholder,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <ImageIcon
                                    color={colors.content.icon.mid}
                                    size={32}
                                />
                                <Text
                                    style={stylesheet.uploadLabel}
                                    variant="bodyMediumSize"
                                >
                                    {localize("events-upload-cover")}
                                </Text>
                                <Text
                                    style={[
                                        stylesheet.uploadDimensions,
                                        {
                                            color: colors.content.text.low
                                        }
                                    ]}
                                    variant="labelSmallSize"
                                >
                                    {localize("events-cover-dimensions")}
                                </Text>
                            </View>
                    }
                </TouchableOpacity>
            </View>
        </View>;
    };

    const renderBasicInfoSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                stylesheet.formCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-basic-info-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-basic-info-subtitle")}
                </Text>
            </View>
            <TextInput
                onChangeText={(text) => setTitle(text)}
                title={localize("events-title-input")}
                spreadBehaviour="stretch"
                placeholder={title}
                value={title}
            />
            <TextAreaInput
                onChangeText={(text) => setDescription(text)}
                title={localize("events-description-input")}
                spreadBehaviour="stretch"
                isUpdateOnRealtime={true}
                placeholder={description}
                value={description}
                numberOfLines={4}
            />
        </View>;
    };

    const renderTypeSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                stylesheet.formCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-type-section-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-type-section-subtitle")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.typeButtonGroup,
                    {
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Button
                    customBorderColor={type === "standart" ? undefined : "secondaryButton"}
                    customColor={type === "standart" ? undefined : "secondaryButton"}
                    customTextColor={colors.project.brandWhite}
                    title={localize("events-type-standard")}
                    onPress={() => setType("standart")}
                    style={stylesheet.typeButton}
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Button
                    customBorderColor={type === "join" ? undefined : "secondaryButton"}
                    customColor={type === "join" ? undefined : "secondaryButton"}
                    customTextColor={colors.project.brandWhite}
                    title={localize("events-type-join")}
                    style={[
                        stylesheet.typeButton,
                        {
                            marginLeft: spaces.spacingMd
                        }
                    ]}
                    onPress={() => setType("join")}
                    spreadBehaviour="free"
                    variant="filled"
                />
            </View>
            {
                type === "join" ?
                    <View
                        style={{
                            gap: spaces.spacingXs
                        }}
                    >
                        <SelectBox
                            titleExtractor={(item: { __title: string; __key: string }) => item.__title}
                            keyExtractor={(item: { __title: string; __key: string }) => item.__key}
                            initialSelectedItems={moduleName ? [
                                {
                                    __title: moduleName === "threeT" ? "3T" : "Modül Yok",
                                    __key: moduleName
                                }
                            ] : undefined}
                            onChange={(selectedItems) => {
                                const item = selectedItems[0];
                                if (item && typeof item.__key === "string") {
                                    setModuleName(item.__key);
                                } else {
                                    setModuleName("");
                                }
                            }}
                            data={[
                                {
                                    __title: "Modül Yok",
                                    __key: ""
                                },
                                {
                                    __key: "threeT",
                                    __title: "3T"
                                }
                            ]}
                            placeholder="Modül Seçimi"
                            spreadBehaviour="stretch"
                            isDisabled={hasSubEvents}
                            title="Etkinlik Modülü"
                        />
                        {
                            hasSubEvents ?
                                <Text
                                    customColor={colors.content.text.danger}
                                    variant="labelSmallSize"
                                >
                                    Bu etkinliğe ait alt etkinlikler olduğu için modül değiştirilemez.
                                </Text>
                            :
                                null
                        }
                    </View>
                :
                    null
            }
        </View>;
    };

    const renderMembersOnlySection = () => {
        return <View
            style={[
                stylesheet.switchRow,
                stylesheet.formCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.switchTextContainer,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-members-only-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-members-only-subtitle")}
                </Text>
            </View>
            <Switch
                onPress={() => setIsOnlyMembers(!isOnlyMembers)}
                customTheme={{
                    themeKey: "events_form" as never
                }}
                isActive={isOnlyMembers}
                type="neutral"
            />
        </View>;
    };

    const renderFooter = () => {
        return <View
            style={[
                stylesheet.footerActionContainer,
                {
                    backgroundColor: colors.project.brandSurface,
                    borderTopColor: colors.project.brandBorder,
                    paddingHorizontal: spaces.spacingXl,
                    paddingVertical: spaces.spacingLg,
                    borderTopWidth: borders.seperator,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.deleteButtonContainer}
            >
                <Button
                    onPress={() => setIsDeleteDialogVisible(true)}
                    title={localize("events-delete-button")}
                    spreadBehaviour="free"
                    type="danger"
                    size="small"
                />
            </View>
            <Button
                onPress={() => navigation.navigate("EventsList")}
                title={localize("events-cancel-button")}
                customBorderColor="secondaryButton"
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
                size="small"
            />
            <Button
                icon={({
                    color,
                    size
                }) => <SaveIcon
                    color={colors.project.brandWhite || colors.content.icon[color]}
                    size={size}
                />}
                customTextColor={colors.project.brandWhite}
                title={localize("events-save-button")}
                customBorderColor="primary"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                spreadBehaviour="free"
                customColor="primary"
                variant="filled"
                size="small"
            />
        </View>;
    };

    if (isLoading) {
        return <PageContainer
            isScrollable={false}
        >
            <View
                style={{
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1
                }}
            >
                <ActivityIndicator
                    color={"gray"}
                    size="large"
                />
            </View>
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.lg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.formBody,
                        {
                            padding: spaces.spacingXl,
                            gap: spaces.spacingXl
                        }
                    ]}
                >
                    {renderImagesSection()}
                    {renderBasicInfoSection()}
                    {renderTypeSection()}
                    <Seperator
                        color="subtle"
                    />
                    {renderMembersOnlySection()}
                </View>
                {renderFooter()}
            </View>
        </View>
        <Dialog
            primaryButtonProps={{
                title: localize("events-create-confirm-yes"),
                onPress: handleConfirmSave
            }}
            secondaryButtonProps={{
                title: localize("events-create-confirm-no"),
                onPress: () => setIsDialogVisible(false),
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onOverlayPress={() => setIsDialogVisible(false)}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("events-edit-confirm-desc")}
                </Text>
            </View>
        </Dialog>
        <Dialog
            primaryButtonProps={{
                icon: ({
                    color,
                    size
                }) => <TrashIcon
                    color={colors.content.icon[color as never] || color}
                    size={size}
                />,
                title: localize("events-delete-confirm-yes"),
                isLoading: isDeleting,
                onPress: handleDelete,
                customColor: "danger"
            }}
            onOverlayPress={() => setIsDeleteDialogVisible(false)}
            secondaryButtonProps={{
                onPress: () => setIsDeleteDialogVisible(false),
                title: localize("events-delete-confirm-no"),
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsDeleteDialogVisible(false)}
            isVisible={isDeleteDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("events-delete-confirm-title")}
                </Text>
            </View>
        </Dialog>
    </PageContainer>;
};

export default EventEdit;
